import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Alert } from "../../components/ui/Alert";
import { BulkActionBar } from "../../components/ui/BulkActionBar";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { DomainFavicon } from "../../components/ui/DomainFavicon";
import { FormField } from "../../components/ui/FormField";
import { Glossary } from "../../components/ui/Glossary";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { Pagination } from "../../components/ui/Pagination";
import { SearchInput } from "../../components/ui/SearchInput";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import { urlGroupsApi } from "../../lib/api/network/urlGroups";
import {
  type URLPoolKind,
  type URLPoolRow,
  type URLVulnStatus,
  urlPoolApi,
} from "../../lib/api/network/urlPool";
import { formatDateTime } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";
import { useRecompileNotice } from "../../lib/recompileNotice";
import { useLocale } from "../../lib/useLocale";

const KINDS: URLPoolKind[] = ["domain", "ipv4", "ipv6", "cidr"];
const STATUSES: URLVulnStatus[] = [
  "unknown",
  "pending",
  "clean",
  "suspicious",
  "malicious",
];

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function vulnBadgeClass(status: URLVulnStatus): string {
  if (status === "clean") return "badge badge--success";
  if (status === "malicious") return "badge badge--danger";
  if (status === "suspicious") return "badge badge--warning";
  if (status === "pending") return "badge badge--info";
  return "badge";
}

export function URLPoolPage() {
  useLocale();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const notice = useRecompileNotice();

  const [kind, setKind] = useState<URLPoolKind | "">("");
  const [vulnStatus, setVulnStatus] = useState<URLVulnStatus | "">("");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [createOpen, setCreateOpen] = useState(false);
  const [createKind, setCreateKind] = useState<URLPoolKind>("domain");
  const [createValue, setCreateValue] = useState("");

  const [editRow, setEditRow] = useState<URLPoolRow | null>(null);
  const [editNote, setEditNote] = useState("");

  const [groupForRows, setGroupForRows] = useState<URLPoolRow[] | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [groupResult, setGroupResult] = useState<string | null>(null);
  const [groupTargetId, setGroupTargetId] = useState<string>("");

  const filtersKey = [kind, vulnStatus, search, limit, offset];
  const list = useQuery({
    queryKey: ["network", "url-pool", ...filtersKey],
    queryFn: () =>
      urlPoolApi.listPage({
        kind: kind || undefined,
        vuln_status: vulnStatus || undefined,
        search: search || undefined,
        limit,
        offset,
      }),
  });

  const urlGroups = useQuery({
    queryKey: ["network", "url-groups"],
    queryFn: () => urlGroupsApi.list(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["network", "url-pool"] });
  };

  const create = useMutation({
    mutationFn: () => urlPoolApi.create({ kind: createKind, value: createValue }),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
      setCreateValue("");
      pushToast({ tone: "success", title: t("operations.domains.created") });
    },
  });

  const updateMut = useMutation({
    mutationFn: (vars: { id: string; note: string }) =>
      urlPoolApi.update(vars.id, { vuln_note: vars.note }),
    onSuccess: () => {
      invalidate();
      setEditRow(null);
    },
  });

  const scanNow = useMutation({
    mutationFn: (id: string) => urlPoolApi.scanNow(id),
    onSuccess: () => {
      invalidate();
      pushToast({
        tone: "info",
        title: t("network.urlPool.action.scanNow"),
        body: t("dashboard.healthOk"),
      });
    },
  });

  const createGroup = useMutation({
    mutationFn: () => urlGroupsApi.create({ name: newGroupName.trim() }),
    onSuccess: (group) => {
      setGroupTargetId(group.id);
      setNewGroupName("");
      void qc.invalidateQueries({ queryKey: ["network", "url-groups"] });
      void qc.invalidateQueries({ queryKey: ["rules", "url-groups"] });
    },
  });

  const addToGroupMut = useMutation({
    mutationFn: async ({ groupId, ids }: { groupId: string; ids: string[] }) => {
      const members = await urlGroupsApi.listMembers(groupId);
      const existing = new Set(members.map((m) => m.url_pool));
      const pending = ids.filter((id) => !existing.has(id));
      const results = await Promise.allSettled(pending.map((id) => urlGroupsApi.addMember(groupId, { url_pool: id })));
      return { failed: pending.filter((_, i) => results[i].status === "rejected"), succeeded: ids.length - results.filter((r) => r.status === "rejected").length };
    },
    onSuccess: (result) => {
      invalidate();
      notice();
      setSelected(new Set(result.failed));
      if (result.failed.length > 0) {
        setGroupForRows((rows) => rows?.filter((r) => result.failed.includes(r.id)) ?? null);
        setGroupResult(formatMessage("operations.domains.partial", { failed: String(result.failed.length), succeeded: String(result.succeeded) }));
      } else {
        setGroupForRows(null);
        setGroupTargetId("");
        setGroupResult(null);
      }
    },
  });

  function openGroups(rows: URLPoolRow[]) {
    setGroupForRows(rows);
    setGroupTargetId("");
    setGroupResult(null);
    setNewGroupName("");
    addToGroupMut.reset();
    createGroup.reset();
  }

  const onCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!createValue.trim()) return;
    create.mutate();
  };

  const resetFiltersOnChange = () => { setOffset(0); setSelected(new Set()); };

  const columns: Column<URLPoolRow>[] = [
    {
      key: "value",
      header: t("network.urlPool.column.value"),
      render: (row) => (
        <div className="domain-cell">
          <DomainFavicon value={row.value} />
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <span className="domain-name domain-name--mono">{row.value}</span>
            {row.vuln_note && (
              <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
                {row.vuln_note}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "kind",
      header: t("network.urlPool.column.kind"),
      render: (row) => (
        <span className="badge badge--info">{t(`network.urlPool.kind.${row.kind}`)}</span>
      ),
    },
    {
      key: "vuln",
      header: t("network.urlPool.column.vulnStatus"),
      render: (row) => (
        <span className={vulnBadgeClass(row.vuln_status)}>
          {t(`network.urlPool.vulnStatus.${row.vuln_status}`)}
        </span>
      ),
    },
    {
      key: "seen",
      header: t("network.urlPool.column.seenCount"),
      render: (row) => <span className="num">{row.seen_count}</span>,
      align: "right",
    },
    {
      key: "rule_usage",
      header: t("network.urlPool.column.usedInRules"),
      render: (row) =>
        row.rule_usage_count && row.rule_usage_count > 0 ? (
          <span className="badge badge--info">{row.rule_usage_count} {t("network.urlPool.rulesShort")}</span>
        ) : (
          <span className="app-pkg">—</span>
        ),
      align: "right",
    },
    {
      key: "last_seen",
      header: t("network.urlPool.column.lastSeen"),
      render: (row) => <span className="mono">{formatDateTime(row.last_seen_at)}</span>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setEditRow(row);
              setEditNote(row.vuln_note ?? "");
            }}
          >
            {t("network.urlPool.action.edit")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openGroups([row]);
            }}
          >
            {t("network.urlPool.action.addToGroup")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            loading={scanNow.isPending && scanNow.variables === row.id}
            onClick={(e) => {
              e.stopPropagation();
              scanNow.mutate(row.id);
            }}
          >
            {t("network.urlPool.action.scanNow")}
          </Button>
        </div>
      ),
    },
  ];

  const selectedRows = useMemo(() => {
    if (!list.data) return [] as URLPoolRow[];
    return list.data.results.filter((r) => selected.has(r.id));
  }, [list.data, selected]);

  return (
    <div>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            {t("nav.domains")}
            <Glossary termKey="urlPool" />
          </span>
        }
        subtitle={t("network.urlPool.subtitle")}
        actions={
          <>
            <Link to="/policies/groups?kind=url">
              <Button variant="secondary">{t("nav.urlGroups")}</Button>
            </Link>
            <Button onClick={() => setCreateOpen(true)}>
              {t("network.urlPool.action.create")}
            </Button>
          </>
        }
      />

      <Alert tone="info" title={t("network.urlPool.explainerTitle")}>
        {t("network.urlPool.explainerBody")}
      </Alert>
      {list.data?.results.some((row) => row.company_decision && row.company_decision !== "inherit") && (
        <Alert tone="warning">{t("operations.domains.legacyWarning")}</Alert>
      )}
      {scanNow.error && <Alert tone="danger">{translateError(scanNow.error)}</Alert>}

      <div className="filter-bar" style={{ marginTop: 16 }}>
        <SearchInput
          placeholder={t("network.urlPool.filter.searchPlaceholder")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetFiltersOnChange();
          }}
        />
        <Select
          value={kind}
          onChange={(e) => {
            setKind((e.target.value as URLPoolKind) || "");
            resetFiltersOnChange();
          }}
          aria-label={t("network.urlPool.filter.kind")}
        >
          <option value="">{t("network.urlPool.filter.kind")}</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`network.urlPool.kind.${k}`)}
            </option>
          ))}
        </Select>
        <Select
          value={vulnStatus}
          onChange={(e) => {
            setVulnStatus((e.target.value as URLVulnStatus) || "");
            resetFiltersOnChange();
          }}
          aria-label={t("network.urlPool.filter.vulnStatus")}
        >
          <option value="">{t("network.urlPool.filter.vulnStatus")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`network.urlPool.vulnStatus.${s}`)}
            </option>
          ))}
        </Select>
      </div>

      <BulkActionBar count={selectedRows.length} onClear={() => setSelected(new Set())}>
        <Button size="sm" variant="secondary" onClick={() => openGroups(selectedRows)}>
          {t("network.urlPool.action.addToGroup")}
        </Button>
      </BulkActionBar>

      <div className="stack">
        <Card padding="none">
          {list.isLoading && <LoadingState />}
          {list.error && (
            <ErrorState
              titleKey="states.errorTitle"
              message={translateError(list.error)}
              action={
                <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
                  {t("common.retry")}
                </Button>
              }
            />
          )}
          {list.data && (
            <>
              <Table
                rows={list.data.results}
                columns={columns}
                rowKey={(row) => row.id}
                selectable
                selectedKeys={selected}
                onSelectionChange={setSelected}
                empty={
                  <EmptyState
                    titleKey="network.urlPool.emptyTitle"
                    bodyKey="network.urlPool.emptyBody"
                  />
                }
              />
              {list.data.count > limit && (
                <Pagination
                  total={list.data.count}
                  limit={limit}
                  offset={offset}
                  onChange={({ limit: l, offset: o }) => {
                    setLimit(l);
                    setOffset(o);
                    setSelected(new Set());
                  }}
                />
              )}
            </>
          )}
        </Card>
      </div>

      {/* Add new URL */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("network.urlPool.create.title")}
      >
        <form onSubmit={onCreate} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {create.error && (
            <Alert tone="danger">{translateError(create.error)}</Alert>
          )}
          <FormField label={t("network.urlPool.create.field.kind")} required>
            {(id) => (
              <select
                id={id}
                className="input"
                value={createKind}
                onChange={(e) => setCreateKind(e.target.value as URLPoolKind)}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`network.urlPool.kind.${k}`)}
                  </option>
                ))}
              </select>
            )}
          </FormField>
          <FormField label={t("network.urlPool.create.field.value")} required>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={createValue}
                onChange={(e) => setCreateValue(e.target.value)}
                placeholder={t("network.urlPool.create.placeholder.value")}
                required
              />
            )}
          </FormField>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="ghost" type="button" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t("network.urlPool.create.submit")}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit note */}
      <Modal
        open={editRow !== null}
        onClose={() => setEditRow(null)}
        title={
          editRow
            ? formatMessage("network.urlPool.edit.title", { value: editRow.value })
            : ""
        }
      >
        {editRow && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateMut.mutate({ id: editRow.id, note: editNote });
            }}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {updateMut.error && (
              <Alert tone="danger">{translateError(updateMut.error)}</Alert>
            )}
            <FormField
              label={t("network.urlPool.edit.field.note")}
              hint={t("network.urlPool.edit.hint.note")}
            >
              {(id) => (
                <textarea
                  id={id}
                  className="input textarea"
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows={4}
                />
              )}
            </FormField>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button variant="ghost" type="button" onClick={() => setEditRow(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={updateMut.isPending}>
                {t("network.urlPool.edit.submit")}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={groupForRows !== null}
        onClose={() => { if (!addToGroupMut.isPending && !createGroup.isPending) setGroupForRows(null); }}
        title={formatMessage("operations.domains.addToGroup", { count: String(groupForRows?.length ?? 0) })}
      >
        {groupForRows && (
          <form className="stack" onSubmit={(event) => {
            event.preventDefault();
            if (groupTargetId && !addToGroupMut.isPending) addToGroupMut.mutate({ groupId: groupTargetId, ids: groupForRows.map((r) => r.id) });
          }}>
            <p className="muted">{t("operations.domains.policyHint")}</p>
            {groupResult && <Alert tone="warning">{groupResult}</Alert>}
            {addToGroupMut.error && <Alert tone="danger">{translateError(addToGroupMut.error)}</Alert>}
            {urlGroups.error && <Alert tone="danger">{translateError(urlGroups.error)}</Alert>}
            <FormField label={t("network.urlPool.addToGroup.field")} required>
              {(id) => (
                <Select id={id} value={groupTargetId} onChange={(e) => setGroupTargetId(e.target.value)} required disabled={addToGroupMut.isPending}>
                  <option value="">{t("common.select")}</option>
                  {urlGroups.data?.filter((g) => g.is_active).map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
                </Select>
              )}
            </FormField>
            <FormField label={t("operations.domains.newGroup")}>
              {(id) => <Input id={id} value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} disabled={createGroup.isPending} />}
            </FormField>
            {createGroup.error && <Alert tone="danger">{translateError(createGroup.error)}</Alert>}
            <Button type="button" variant="secondary" disabled={!newGroupName.trim() || addToGroupMut.isPending} loading={createGroup.isPending} onClick={() => createGroup.mutate()}>
              {t("network.urlGroups.action.create")}
            </Button>
            <Button type="submit" disabled={!groupTargetId || createGroup.isPending} loading={addToGroupMut.isPending}>
              {t("network.urlPool.addToGroup.submit")}
            </Button>
          </form>
        )}
      </Modal>
    </div>
  );
}
